import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaHome, FaSearch, FaTimes } from 'react-icons/fa';
import PropertyPortfolioDashboard from './PropertyPortfolioDashboard';
import PropertyWorkspacePane from './PropertyWorkspacePane';
import { fetchPropertyList, PORTFOLIO_TAB_ID } from './propertyApi';
import { PROPERTY_TYPE_FILTERS } from './mockPropertyData';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;
const MAX_OPEN_TABS = 15;

function PropertyExplorerTab() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [properties, setProperties] = useState([]);
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(PORTFOLIO_TAB_ID);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabLimitMessage, setTabLimitMessage] = useState(null);

  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const loadProperties = useCallback(async (q, propertyType) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const rows = await fetchPropertyList({
        q,
        propertyType,
        signal: controller.signal,
      });

      if (controller.signal.aborted) {
        return;
      }

      setProperties(rows);
      setOpenTabs(prev =>
        prev.map(tab => {
          const updated = rows.find(row => row.id === tab.id);
          return updated ? { ...tab, title: updated.name, property: updated } : tab;
        })
      );
    } catch (err) {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Property list load failed:', err);
      setError(err?.message || 'Failed to load properties');
      setProperties([]);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = searchTerm.trim();
    const effectiveQ = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    const delay = trimmed.length > 0 ? SEARCH_DEBOUNCE_MS : 0;

    debounceRef.current = setTimeout(() => {
      loadProperties(effectiveQ, typeFilter);
    }, delay);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, typeFilter, loadProperties]);

  useEffect(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    },
    []
  );

  const openTabIds = useMemo(
    () => new Set(openTabs.map(tab => tab.id)),
    [openTabs]
  );

  const openPropertyTab = useCallback(property => {
    if (!property?.id) {
      return;
    }

    setTabLimitMessage(null);
    setOpenTabs(prev => {
      const exists = prev.some(tab => tab.id === property.id);
      if (exists) {
        setActiveTabId(property.id);
        return prev.map(tab =>
          tab.id === property.id
            ? { ...tab, title: property.name, property }
            : tab
        );
      }
      if (prev.length >= MAX_OPEN_TABS) {
        setTabLimitMessage(
          `You can open at most ${MAX_OPEN_TABS} properties at once. Close a tab to open another.`
        );
        return prev;
      }
      setActiveTabId(property.id);
      return [
        ...prev,
        {
          id: property.id,
          title: property.name,
          property,
          innerTab: 'detail',
        },
      ];
    });
  }, []);

  const closeTab = useCallback((recordId, event) => {
    event?.stopPropagation();
    event?.preventDefault();
    setTabLimitMessage(null);
    setOpenTabs(prev => {
      const nextTabs = prev.filter(tab => tab.id !== recordId);
      setActiveTabId(currentActive => {
        if (currentActive !== recordId) {
          return currentActive;
        }
        if (nextTabs.length === 0) {
          return PORTFOLIO_TAB_ID;
        }
        const closedIndex = prev.findIndex(tab => tab.id === recordId);
        const nextIndex = Math.min(closedIndex, nextTabs.length - 1);
        return nextTabs[nextIndex]?.id ?? PORTFOLIO_TAB_ID;
      });
      return nextTabs;
    });
  }, []);

  const setInnerTab = useCallback((recordId, innerTab) => {
    setOpenTabs(prev =>
      prev.map(tab => (tab.id === recordId ? { ...tab, innerTab } : tab))
    );
  }, []);

  const setSelectedUnit = useCallback((propertyId, unitId) => {
    setOpenTabs(prev =>
      prev.map(tab => (tab.id === propertyId ? { ...tab, selectedUnitId: unitId } : tab))
    );
  }, []);

  const handleOpenUnit = useCallback(
    ({ propertyId, propertyName, unitId }) => {
      if (!propertyId) {
        return;
      }

      let property =
        properties.find(row => row.id === propertyId) ||
        openTabs.find(tab => tab.id === propertyId)?.property;

      if (!property) {
        property = {
          id: propertyId,
          name: propertyName || 'Property',
          status: 'Active',
          totalFloors: 0,
          totalUnits: 0,
        };
      }

      openPropertyTab(property);
      setInnerTab(propertyId, 'floors');
      if (unitId) {
        setSelectedUnit(propertyId, unitId);
      }
    },
    [properties, openTabs, openPropertyTab, setInnerTab, setSelectedUnit]
  );

  const isPortfolioActive = activeTabId === PORTFOLIO_TAB_ID;

  const searchHint =
    searchTerm.trim().length > 0 && searchTerm.trim().length < MIN_SEARCH_LENGTH
      ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
      : null;

  return (
    <div className='re-explorer'>
      <aside className='re-explorer__sidebar'>
        <div className='re-explorer__sidebar-header'>
          <h3>Properties</h3>
          <span className='re-explorer__count'>
            {loading ? '…' : `${properties.length} found`}
          </span>
        </div>

        <div className='re-explorer__search'>
          <InputGroup size='sm'>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type='search'
              placeholder='Search name, code, city…'
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              aria-label='Search properties'
            />
          </InputGroup>
          {searchHint && <div className='re-explorer__search-hint'>{searchHint}</div>}
        </div>

        <div className='re-explorer__filters' role='group' aria-label='Property type filters'>
          {PROPERTY_TYPE_FILTERS.map(filter => (
            <button
              key={filter}
              type='button'
              className={`re-filter-chip${typeFilter === filter ? ' active' : ''}`}
              onClick={() => setTypeFilter(filter)}
              disabled={loading}
            >
              {filter}
            </button>
          ))}
        </div>

        {error && (
          <Alert variant='danger' className='m-2 py-2 px-3' style={{ fontSize: '13px' }}>
            {error}
          </Alert>
        )}

        <div className='re-explorer__list'>
          {loading && properties.length === 0 ? (
            <div className='re-explorer__loading'>
              <Spinner animation='border' size='sm' role='status' />
              <span>Loading properties…</span>
            </div>
          ) : properties.length === 0 ? (
            <div className='re-explorer__empty-list'>
              {searchTerm.trim().length >= MIN_SEARCH_LENGTH
                ? 'No properties match your search.'
                : 'No active properties found.'}
            </div>
          ) : (
            properties.map(property => {
              const isActive = property.id === activeTabId;
              const isOpen = openTabIds.has(property.id);
              const statusKey = (property.status || 'active')
                .replace(/\s+/g, '-')
                .toLowerCase();
              return (
                <button
                  key={property.id}
                  type='button'
                  className={`re-property-item${isActive ? ' active' : ''}${isOpen && !isActive ? ' open' : ''}`}
                  onClick={() => openPropertyTab(property)}
                >
                  <div className='re-property-item__title'>{property.name}</div>
                  <div className='re-property-item__subtitle'>
                    {[property.propertyCode, property.city].filter(Boolean).join(' · ')}
                  </div>
                  <div className='re-property-item__meta'>
                    <span>{property.propertyType || '—'}</span>
                    {property.status && (
                      <span className={`re-status-pill re-status-pill--${statusKey}`}>
                        {property.status}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <div className='re-explorer__panel'>
        <div className='re-property-tabs' role='tablist' aria-label='Real estate workspace'>
          <div
            role='tab'
            aria-selected={isPortfolioActive}
            className={`re-property-tab re-property-tab--portfolio${isPortfolioActive ? ' active' : ''}`}
          >
            <button
              type='button'
              className='re-property-tab__label'
              title='Portfolio overview'
              onClick={() => setActiveTabId(PORTFOLIO_TAB_ID)}
            >
              <FaHome className='re-property-tab__home-icon' aria-hidden='true' />
              Portfolio
            </button>
          </div>
          {openTabs.map(tab => {
            const isTabActive = activeTabId === tab.id;
            return (
              <div
                key={tab.id}
                role='tab'
                aria-selected={isTabActive}
                className={`re-property-tab${isTabActive ? ' active' : ''}`}
              >
                <button
                  type='button'
                  className='re-property-tab__label'
                  title={tab.title}
                  onClick={() => setActiveTabId(tab.id)}
                >
                  {tab.title}
                </button>
                <button
                  type='button'
                  className='re-property-tab__close'
                  title='Close tab'
                  aria-label={`Close ${tab.title}`}
                  onClick={event => closeTab(tab.id, event)}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            );
          })}
        </div>
        {tabLimitMessage && (
          <div className='re-property-tabs__notice'>{tabLimitMessage}</div>
        )}
        <div className='re-property-tab-content'>
          <div
            className={`re-property-tab-pane${isPortfolioActive ? '' : ' hidden'}`}
            role='tabpanel'
            hidden={!isPortfolioActive}
          >
            <PropertyPortfolioDashboard
              propertyType={typeFilter}
              onOpenUnit={handleOpenUnit}
            />
          </div>
          {openTabs.map(tab => (
            <div
              key={tab.id}
              className={`re-property-tab-pane${activeTabId === tab.id ? '' : ' hidden'}`}
              role='tabpanel'
              hidden={activeTabId !== tab.id}
            >
              <PropertyWorkspacePane
                property={tab.property}
                innerTab={tab.innerTab || 'detail'}
                onInnerTabChange={innerTab => setInnerTab(tab.id, innerTab)}
                selectedUnitId={tab.selectedUnitId || null}
                onSelectUnit={unitId => setSelectedUnit(tab.id, unitId)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PropertyExplorerTab;
